import { extend, findIndex, debounce, getStringWidth } from "@dhx/ts-common/core";
import { el, awaitRedraw } from "@dhx/ts-common/dom";
import { EventSystem } from "@dhx/ts-common/events";
import { locate, isIE } from "@dhx/ts-common/html";
import { KeyManager, IKeyManager } from "@dhx/ts-common/KeyManager";
import { focusManager } from "@dhx/ts-common/FocusManager";
import { Layout, LayoutEvents, ILayoutEventHandlersMap } from "@dhx/ts-layout";
import { ITabbarConfig, TabbarEvents, ITabbarEventHandlersMap, ITabbar, ITab } from "./types";

export class Tabbar extends Layout implements ITabbar {
	public config: ITabbarConfig;
	public events: EventSystem<
		TabbarEvents | LayoutEvents,
		ITabbarEventHandlersMap | ILayoutEventHandlersMap
	>;
	private _tabsContainer: Element;
	protected _cells: ITab[];
	private _beforeScrollSize: number;
	private _afterScrollSize: number;
	private _keyManager: IKeyManager;

	constructor(container, config: ITabbarConfig) {
		super(container, extend({ mode: "top" }, config));
		this._keyManager = new KeyManager(() => locate(document.activeElement, "tabs_id") === this._uid);
		this._initHotkeys();
		if (this.config.disabled) {
			const { disabled } = this.config;
			const exsistId = this._cells.map(tab => {
				return tab.id;
			});
			if (Array.isArray(disabled)) {
				disabled.forEach(tab => {
					if (exsistId.includes(tab) && !this._disabled.includes(tab)) {
						this._disabled.push(tab);
					}
				});
			} else if (exsistId.includes(disabled) && !this._disabled.includes(disabled)) {
				this._disabled.push(disabled);
			}
			this.paint();
		}
		this.events = new EventSystem<TabbarEvents>(this);
		focusManager.setFocusId(this._uid);
	}
	toVDOM(): any {
		this._getTabContainer();
		let activeView = null;
		if (!this.config.noContent) {
			activeView = this.getCell(this.config.activeView);
			if (activeView) {
				const disabled = this._disabled.includes(this.config.activeView)
					? " dhx_tabbar-content--disabled"
					: "";
				if (activeView.config.css) {
					if (activeView.config.css.indexOf("dhx_tabbar-content--disabled") !== -1) {
						activeView.config.css = activeView.config.css.replace(
							"dhx_tabbar-content--disabled",
							""
						);
					} else {
						activeView.config.css = activeView.config.css + disabled;
					}
				} else {
					activeView.config.css = disabled;
				}
			}
		}

		awaitRedraw().then(() => {
			if (!this._tabsContainer) {
				this.paint();
			}
		});

		return el(
			"div",
			{
				class:
					"dhx_widget dhx_tabbar" +
					(this.config.mode ? " dhx_tabbar--" + this.config.mode : "") +
					(this.config.css ? " " + this.config.css : ""),
			},
			this._tabsContainer ? [...this._drawTabs(), activeView ? activeView.toVDOM() : null] : []
		);
	}
	destructor(): void {
		this.events && this.events.clear();
		this._keyManager && this._keyManager.destructor();

		super.destructor();
		this.unmount();

		this._tabsContainer = this._beforeScrollSize = this._afterScrollSize = this._keyManager = null;
		this.config = this.events = this._cells = this._handlers = null;
	}
	getWidget(): any {
		const activeCell = this._cells.filter(cell => this.getActive() === cell.id);
		return activeCell[0].getWidget();
	}
	setActive(id: string): void {
		const exsistId = this._cells.map(tab => {
			return tab.id;
		});
		if (exsistId.includes(id) && !this._disabled.includes(id)) {
			const prev = this.config.activeView;
			if (!this.events.fire(TabbarEvents.beforeChange, [id, prev])) {
				return;
			}
			this.config.activeView = id;
			this.getCell(id).show();
			this._focusTab(id);
			this.events.fire(TabbarEvents.change, [id, prev]);
		}
	}
	getActive(): string {
		return this.config ? this.config.activeView : null;
	}
	addTab(config: ITabbarConfig, index: number): void {
		this.addCell(config, index);
		if (this._cells.length === 1 && !config.disabled) {
			this.setActive(this._cells[0].id);
		}
	}
	removeTab(id: string): void {
		if (!this.events.fire(TabbarEvents.beforeClose, [id])) {
			return;
		}
		if (id === this.config.activeView) {
			const cellLength = this._getEnableTabs().length;
			let index = findIndex(this._getEnableTabs(), cell => cell.id === this.config.activeView);
			if (index < 0) {
				return;
			}
			if (index === cellLength - 1) {
				index = index - 1;
			}
			super.removeCell(id);
			if (cellLength === 1) {
				this.config.activeView = null;
			} else {
				this.setActive(this._getEnableTabs()[index].id);
			}
		} else {
			super.removeCell(id);
		}
		this.events.fire(TabbarEvents.afterClose, [id]);
		this.events.fire(TabbarEvents.close, [id]); // TODO: remove suite_7.0
	}
	disableTab(id: string): boolean {
		const exsistId = this._cells.map(tab => {
			return tab.id;
		});
		if (exsistId.includes(id) && !this._disabled.includes(id)) {
			this._disabled.push(id);
			this.paint();
			return true;
		}
		return false;
	}
	enableTab(id: string): void {
		if (this._disabled.includes(id)) {
			const sort = this._disabled.filter(tab => tab !== id);
			this._disabled = [...sort];
			this.paint();
		}
	}
	isDisabled(id?: string): boolean {
		return this._disabled.includes(id ? id : this.config.activeView);
	}
	/** @deprecated See a documentation: https://docs.dhtmlx.com/ */
	removeCell(id: string): void {
		this.removeTab(id);
	}
	protected _initHandlers() {
		super._initHandlers();
		this._handlers = {
			...this._handlers,
			onTabClick: e => {
				awaitRedraw().then(() => {
					const tabId = locate(e, "dhx_tabid");
					if (!tabId || this._disabled.includes(tabId)) {
						return;
					}
					const prev = this.config.activeView;

					if (e.target.classList.contains("dhx_tabbar-tab__close")) {
						this.removeTab(tabId);
					} else {
						if (!this.events.fire(TabbarEvents.beforeChange, [tabId, prev])) {
							return;
						}
						this.config.activeView = tabId;
						if (prev !== tabId)
							this.events.fire(TabbarEvents.change, [this.config.activeView, prev]);
					}
					this.paint();
				});
			},
			onScrollClick: e => {
				let mode = locate(e, "mode");
				if (!mode) {
					switch (e.key) {
						case "ArrowRight":
							mode = "right";
							break;
						case "ArrowLeft":
							mode = "left";
							break;
						case "ArrowUp":
							mode = "up";
							break;
						case "ArrowDown":
							mode = "down";
							break;
					}
				}
				const options: any = {
					behavior: "smooth",
				};

				if (this._isHorizontalMode()) {
					let firstCellWidth = this._normalizeSize({
						width: this._getSizes(this._cells[0].config).width,
					}).width;

					let lastCellWidth = this._normalizeSize({
						width: this._getSizes(this._cells[this._cells.length - 1].config).width,
					}).width;

					let totalWidth;
					if (this._tabsContainer) {
						totalWidth = this._tabsContainer.scrollWidth;

						this._cells.reduce((sum, tab, i) => {
							if (sum >= this._tabsContainer.scrollLeft && i !== 0 && mode === "left") {
								firstCellWidth = Math.abs(
									this._normalizeSize({
										width: this._getSizes(this._cells[i - 1].config).width,
									}).width -
										(sum - this._tabsContainer.scrollLeft)
								);
							} else if (
								sum > totalWidth + this._tabsContainer.scrollLeft &&
								mode === "right"
							) {
								lastCellWidth = Math.abs(totalWidth + this._tabsContainer.scrollLeft - sum);
							} else {
								return (
									sum +
									this._normalizeSize({ width: this._getSizes(tab.config).width }).width
								);
							}
						}, 0);
					}

					options.left =
						mode === "left"
							? this._tabsContainer.scrollLeft - firstCellWidth
							: this._tabsContainer.scrollLeft + lastCellWidth;
				} else {
					let firstCellHeight = this._normalizeSize({
						height: this._getSizes(this._cells[0].config).height,
					}).height;

					let lastCellHeight = this._normalizeSize({
						height: this._getSizes(this._cells[this._cells.length - 1].config).height,
					}).height;

					let totalHeight;
					if (this._tabsContainer) {
						totalHeight = this._tabsContainer.clientHeight;

						this._cells.reduce((sum, tab, i) => {
							if (sum >= this._tabsContainer.scrollTop && i !== 0 && mode === "up") {
								firstCellHeight = Math.abs(
									this._normalizeSize({
										height: this._getSizes(this._cells[i - 1].config).height,
									}).height -
										(sum - this._tabsContainer.scrollTop)
								);
							} else if (sum > totalHeight + this._tabsContainer.scrollTop && mode === "down") {
								lastCellHeight = Math.abs(totalHeight + this._tabsContainer.scrollTop - sum);
							} else {
								return (
									sum +
									this._normalizeSize({ height: this._getSizes(tab.config).height }).height
								);
							}
						}, 0);
					}

					options.top =
						mode === "up"
							? this._tabsContainer.scrollTop - firstCellHeight
							: this._tabsContainer.scrollTop + lastCellHeight;
				}

				if (isIE()) {
					this._tabsContainer.scrollLeft = options.left;
					this._tabsContainer.scrollTop = options.top;
				} else {
					this._tabsContainer.scrollTo(options);
				}
			},
			onHeaderScroll: debounce(() => {
				this.paint();
			}, 10),
		};
	}
	private _isHorizontalMode() {
		return this.config.mode === "bottom" || this.config.mode === "top";
	}
	private _focusTab(id: string, e?: Event) {
		awaitRedraw().then(() => {
			this.getRootView().refs[id].el.focus();
			e && this._handlers.onScrollClick(e);
		});
	}
	private _getEnableTabs() {
		return this._cells.filter(tab => !this._disabled.includes(tab.config.id));
	}
	private _getIndicatorPosition() {
		let activeIndex = findIndex(this._cells, cell => cell.id === this.config.activeView);
		if (activeIndex === -1) {
			activeIndex = 0;
		}
		const activeCell = this.getCell(this.config.activeView);
		if (this._isHorizontalMode()) {
			const { width, unit } = this._normalizeSize({
				width: this._getSizes(activeCell.config).width,
			});
			const totalWidth = this._tabsContainer.clientWidth;
			const translateX = this._cells.reduce((sum, item, i) => {
				const size = this._normalizeSize({ width: this._getSizes(item.config).width });
				if (size.unit === "%") {
					size.width = (totalWidth / 100) * size.width;
				}
				return i < activeIndex ? sum + size.width : sum;
			}, 0);
			return {
				left: 0,
				transform: `translateX(${translateX}px)`,
				transition: "all 0.1s ease",
				width: width + unit,
				height: "2px",
			};
		} else {
			const { height, unit } = this._normalizeSize({
				height: this._getSizes(activeCell.config).height,
			});
			const totalHeight = this._tabsContainer.clientHeight;
			const translateY = this._cells.reduce((sum, item, i) => {
				const size = this._normalizeSize({ height: this._getSizes(item.config).height });
				if (size.unit === "%") {
					size.height = (totalHeight / 100) * size.height;
				}
				return i < activeIndex ? sum + size.height : sum;
			}, 0);
			return {
				top: 0,
				transform: `translateY(${translateY}px)`,
				transition: "all 0.1s ease",
				height: height + unit,
				width: "2px",
			};
		}
	}
	private _drawTabs() {
		if (!this._cells.length) {
			return [];
		}

		let arrowsStyle;
		let totalSize;
		let totalTabsSize;
		this._beforeScrollSize = 0;
		this._afterScrollSize = 0;
		const isHorizontal = this._isHorizontalMode();

		if (isHorizontal) {
			totalSize = this._tabsContainer.clientWidth;
			totalTabsSize = Math.round(
				this._cells.reduce((sum, tab) => {
					return this._normalizeSize({ width: this._getSizes(tab.config).width }).width + sum;
				}, 0)
			);
			if (this._tabsContainer && totalTabsSize >= totalSize) {
				this._beforeScrollSize = this._tabsContainer.scrollLeft;
				this._afterScrollSize = totalTabsSize - (totalSize + this._beforeScrollSize);
			} else if (totalTabsSize >= totalSize) {
				this._afterScrollSize = totalTabsSize - totalSize;
			}
			arrowsStyle = {
				height: this.config.tabHeight || "45px",
				top: this.config.mode === "top" ? 0 : "",
			};
		} else {
			totalSize = this._tabsContainer.clientHeight;
			totalTabsSize = Math.round(
				this._cells.reduce((sum, tab) => {
					return this._normalizeSize({ height: this._getSizes(tab.config).height }).height + sum;
				}, 0)
			);
			if (this._tabsContainer && totalTabsSize >= totalSize) {
				this._beforeScrollSize = this._tabsContainer.scrollTop;
				this._afterScrollSize = totalTabsSize - (totalSize + this._beforeScrollSize);
			} else {
				this._afterScrollSize = totalTabsSize - totalSize;
			}
			arrowsStyle = {
				width: this.config.tabWidth || "200px",
				left: this.config.mode === "left" ? 0 : "",
			};
		}

		const headerStyle = this._getIndicatorPosition();
		return [
			el(
				".dhx_tabbar-header__wrapper",
				{
					onscroll: this._handlers.onHeaderScroll,
					class:
						this.config.tabAlign && this._beforeScrollSize <= 0 && this._afterScrollSize <= 0
							? `dhx_tabbar-header__wrapper-${this.config.tabAlign}`
							: "",
				},
				[
					el(
						"ul" + "." + this.config.mode,
						{
							tabs_id: this._uid,
							class: "dhx_tabbar-header ",
							onclick: this._handlers.onTabClick,
						},
						[
							...this._cells.map((cell: ITab) => {
								const { closable, closeButtons, activeView } = this.config;
								return el(
									"li",
									{
										class:
											"dhx_tabbar-tab" +
											(cell.config.tabCss ? ` ${cell.config.tabCss}` : ""),
										dhx_tabid: cell.id,
										role: "presentation",
										style: this._getSizes(cell.config),
									},
									[
										el(
											"button.dhx_button.dhx_tabbar-tab-button" +
												(activeView === cell.id
													? ".dhx_tabbar-tab-button--active"
													: "") +
												(this._disabled.includes(cell.config.id)
													? ".dhx_tabbar-tab-button--disabled"
													: ""),
											{
												tabindex: "0",
												"aria-controls": cell.id,
												id: "tab-content-" + cell.id,
												"aria-selected": `${activeView === cell.id}`,
												_ref: cell.id.toString(),
												type: "button",
											},
											[el("span.dhx_button__text", cell.config.tab)]
										),
										(Array.isArray(closable) &&
											closable.includes(cell.config.id) &&
											!this._disabled.includes(cell.config.id)) ||
										(closable &&
											typeof closable === "boolean" &&
											!this._disabled.includes(cell.config.id)) ||
										(closeButtons &&
											typeof closeButtons === "boolean" &&
											!this._disabled.includes(cell.config.id))
											? el("div.dhx_tabbar-tab__close.dxi--small.dxi.dxi-close", {
													tabindex: 0,
													role: "button",
													"aria-pressed": "false",
											  })
											: null,
									]
								);
							}),
							el(".dhx_tabbar-header-active", {
								style: headerStyle,
							}),
						]
					),
				]
			),
			this._beforeScrollSize > 0 &&
				el(".dhx_tabbar_scroll", {
					class: `dxi dxi-chevron-${isHorizontal ? "left" : "up"} arrow-${
						isHorizontal ? "left" : "up"
					}`,
					_key: "startArrow",
					onclick: this._handlers.onScrollClick,
					mode: isHorizontal ? "left" : "up",
					style: arrowsStyle,
				}),
			this._afterScrollSize > 0 &&
				el(".dhx_tabbar_scroll", {
					class: `dxi dxi-chevron-${isHorizontal ? "right" : "down"} arrow-${
						isHorizontal ? "right" : "down"
					}`,
					_key: "endArrow",
					onclick: this._handlers.onScrollClick,
					mode: isHorizontal ? "right" : "down",
					style: arrowsStyle,
				}),
		];
	}
	private _getSizes(config) {
		if (typeof config.tabWidth === "number") config.tabWidth = config.tabWidth + "px";
		if (typeof config.tabHeight === "number") config.tabHeight = config.tabHeight + "px";

		if (typeof this.config.tabWidth === "number") this.config.tabWidth = this.config.tabWidth + "px";
		if (typeof this.config.tabHeight === "number") this.config.tabHeight = this.config.tabHeight + "px";

		let width =
			this.config.tabWidth ||
			(this._isHorizontalMode()
				? getStringWidth(config.tab.toUpperCase(), { font: "500 14.4px Arial" }) + 50 + "px"
				: "200px");
		let height = this.config.tabHeight || "45px";

		if (this._isHorizontalMode()) {
			if (config.tabWidth !== undefined) {
				width = config.tabWidth;
			}
		} else {
			if (config.tabHeight !== undefined) {
				height = config.tabHeight;
			}
		}

		if (
			((this.config.tabAutoWidth && config.tabAutoWidth !== false) || config.tabAutoWidth) &&
			this.config.tabWidth === undefined &&
			config.tabWidth === undefined
		) {
			width = this._getTabAutoWidth();
		}

		if (
			((this.config.tabAutoHeight && config.tabAutoHeight !== false) || config.tabAutoHeight) &&
			this.config.tabHeight === undefined &&
			config.tabHeight === undefined
		) {
			height = this._getTabAutoHeight();
		}

		return { width, height } as any;
	}
	private _normalizeSize(size?: { width?: string | number; height?: string | number }) {
		const sizes: any = {};

		if (Object.keys(size).length >= 1) {
			for (const key in size) {
				if (typeof size[key] === "number") {
					sizes.unit = "px";
				} else {
					if (size[key].includes("%")) {
						sizes[key] = size[key].slice(0, -1);
						sizes.unit = "%";
					} else if (size[key].includes("px")) {
						sizes[key] = size[key].slice(0, -2);
						sizes.unit = "px";
					}
					sizes[key] = parseFloat(sizes[key]);
				}
			}
		}

		return sizes;
	}
	private _getTabAutoWidth() {
		const totalWidth = this._tabsContainer.clientWidth;
		let frozenWidth = 0;
		let autoTabs = 0;
		this._cells.forEach(cell => {
			if (
				cell.config.tabAutoWidth ||
				(this.config.tabAutoWidth && cell.config.tabAutoWidth !== false)
			) {
				if (cell.config.tabWidth) {
					frozenWidth += this._normalizeSize({ width: cell.config.tabWidth }).width;
				} else {
					autoTabs++;
				}
			} else {
				frozenWidth += this._normalizeSize({ width: this._getSizes(cell.config).width }).width;
			}
		});

		return (totalWidth - frozenWidth) / autoTabs + "px";
	}
	private _getTabAutoHeight() {
		const totalHeight = this._tabsContainer.clientHeight;
		let frozenHeight = 0;
		let autoTabs = 0;
		this._cells.forEach(cell => {
			if (
				cell.config.tabAutoHeight ||
				(this.config.tabAutoHeight && cell.config.tabAutoHeight !== false)
			) {
				if (cell.config.tabHeight) {
					frozenHeight += this._normalizeSize({ height: cell.config.tabHeight }).height;
				} else {
					autoTabs++;
				}
			} else {
				frozenHeight += this._normalizeSize({ height: this._getSizes(cell.config).height }).height;
			}
		});

		return (totalHeight - frozenHeight) / autoTabs + "px";
	}
	private _getTabContainer() {
		if (this._tabsContainer) {
			if (!this.getRootNode()) {
				awaitRedraw().then(() => this.paint());
			} else {
				const headerWrapper =
					this.getRootNode() &&
					this.getRootNode().getElementsByClassName("dhx_tabbar-header__wrapper")[0];

				if (headerWrapper && this._tabsContainer !== headerWrapper) {
					this._tabsContainer = headerWrapper;
					this.paint();
				}
			}
		} else {
			this._tabsContainer = this.getRootNode();
			this.paint();
		}
	}
	private _initHotkeys() {
		const activeNextTab = e => {
			e.preventDefault();
			const enableTabs = this._getEnableTabs();
			const activeIndex = findIndex(enableTabs, cell => cell.id === this.config.activeView);
			const prev = this.config.activeView;
			if (activeIndex === -1) {
				return;
			}
			const tabId =
				activeIndex === enableTabs.length - 1 ? enableTabs[0].id : enableTabs[activeIndex + 1].id;
			if (!this.events.fire(TabbarEvents.beforeChange, [tabId, prev])) {
				return;
			}
			this.config.activeView = tabId;
			this.events.fire(TabbarEvents.change, [this.config.activeView, prev]);
			this._focusTab(this.config.activeView, e);
			this.paint();
		};

		const activePrevTab = e => {
			e.preventDefault();
			const enableTabs = this._getEnableTabs();
			const activeIndex = findIndex(enableTabs, cell => cell.id === this.config.activeView);
			const prev = this.config.activeView;
			if (activeIndex === -1) {
				return;
			}
			const tabId =
				activeIndex === 0 ? enableTabs[enableTabs.length - 1].id : enableTabs[activeIndex - 1].id;
			if (!this.events.fire(TabbarEvents.beforeChange, [tabId, prev])) {
				return;
			}
			this.config.activeView = tabId;
			this.events.fire(TabbarEvents.change, [this.config.activeView, prev]);
			this._focusTab(this.config.activeView, e);
			this.paint();
		};

		const isVertical = this.config.mode === "right" || this.config.mode === "left";

		const handlers = {
			arrowRight: activeNextTab,
			arrowUp: isVertical ? activePrevTab : activeNextTab,
			arrowLeft: activePrevTab,
			arrowDown: isVertical ? activeNextTab : activePrevTab,
		};

		for (const key in handlers) {
			this._keyManager.addHotKey(key, handlers[key]);
		}
	}
}
